import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Filter, Plus, Copy, ExternalLink, Users, Loader2, HelpCircle, Sparkles } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function Funnels() {
  const [funnels, setFunnels] = useState(null);
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [quizLoading, setQuizLoading] = useState(null);

  const load = () => api.get("/funnels").then((r) => setFunnels(r.data)).catch(() => setFunnels([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title.trim()) return toast.error("Ponle un nombre a tu embudo");
    setCreating(true);
    try {
      await api.post("/funnels", { title });
      setTitle("");
      toast.success("Embudo + cuestionario creados con IA");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const genQuiz = async (id) => {
    setQuizLoading(id);
    try {
      await api.post(`/funnels/${id}/quiz`);
      toast.success("Cuestionario generado");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setQuizLoading(null);
    }
  };

  const publicUrl = (slug) => `${window.location.origin}/f/${slug}`;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Enlace copiado"); };

  return (
    <div data-testid="funnels-page">
      <div className="flex items-center gap-3 mb-2">
        <Filter className="text-purple-500" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Embudos con Cuestionario</h1>
      </div>
      <p className="text-zinc-400 mb-8">Cuestionarios divertidos que la IA usa para perfilar y enviar a tu WhatsApp solo a los mejores prospectos.</p>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 mb-8">
        <Label className="text-zinc-300">Nombre del nuevo embudo</Label>
        <div className="flex flex-col sm:flex-row gap-3 mt-2">
          <Input
            data-testid="funnel-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej. Mamás emprendedoras"
            className="bg-black border-white/15 rounded-xl py-6 focus-visible:ring-purple-500"
          />
          <Button data-testid="create-funnel-button" onClick={create} disabled={creating} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-6 whitespace-nowrap">
            {creating ? <Loader2 className="animate-spin mr-2" size={18} /> : <Plus size={18} className="mr-2" />} Crear con IA
          </Button>
        </div>
      </div>

      {funnels === null && <p className="text-zinc-500">Cargando...</p>}
      {funnels?.length === 0 && <p className="text-zinc-500">Aún no tienes embudos.</p>}

      <div className="grid sm:grid-cols-2 gap-5">
        {funnels?.map((f) => (
          <div key={f.funnel_id} data-testid="funnel-card" className="rounded-2xl border border-white/10 bg-[#121212] p-6">
            <h3 className="font-display font-bold text-lg">{f.title}</h3>
            <p className="text-purple-300 text-sm mt-1 font-semibold">{f.headline}</p>
            <p className="text-zinc-400 text-sm mt-1">{f.subheadline}</p>
            <div className="flex items-center gap-4 mt-4 text-sm text-zinc-400">
              <span className="flex items-center gap-1.5"><Users size={16} className="text-yellow-400" /> {f.leads_count} prospecto(s)</span>
              <span className="flex items-center gap-1.5"><HelpCircle size={16} className="text-purple-400" /> {f.questions?.length || 0} preguntas</span>
            </div>
            {(!f.questions || f.questions.length === 0) && (
              <Button data-testid="gen-quiz-button" onClick={() => genQuiz(f.funnel_id)} disabled={quizLoading === f.funnel_id} variant="outline" className="mt-4 w-full border-purple-500/40 bg-transparent hover:bg-purple-600 rounded-xl">
                {quizLoading === f.funnel_id ? <Loader2 className="animate-spin mr-2" size={16} /> : <Sparkles size={16} className="mr-2" />} Generar cuestionario
              </Button>
            )}
            <div className="flex gap-2 mt-4">
              <button data-testid="copy-funnel-link" onClick={() => copy(publicUrl(f.slug))} className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm bg-white/5 hover:bg-white/10 rounded-xl py-2.5 border border-white/10">
                <Copy size={14} /> Copiar enlace
              </button>
              <a href={publicUrl(f.slug)} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm bg-purple-600 hover:bg-purple-500 rounded-xl py-2.5">
                <ExternalLink size={14} /> Ver página
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
